// Decision Review — matches mockup #1 panel 2: the focused decision transform with
// Evidence / Predictions / Expected Outcomes / History (+ a placeholder Notes tab).
// REAL: /executive/queue item, /outcomes (evidence/claims/expected/versions),
// /predictions (correlation-linked), /evaluations, /journal. PLACEHOLDER: evidence
// relevance %, Notes (mockData).
import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useBrainResource } from "../lib/hooks";
import { versionsForSubject, detectSupersede, detectConfidenceDelta, detectConflict, detectRelevantKnowledge } from "../lib/moments";
import { formatDecisionImpact } from "../lib/impactBasis";
import { LoadingState, EmptyState, DisabledState } from "../BrainStates";
import MomentStrip from "../MomentStrip";
import EvidenceTrail from "../regions/EvidenceTrail";
import { MOCK_NOTES } from "../mock/mockData";

function d(r) { return r && r.status === "ok" ? r.data : null; }
function list(r, key) { const x = d(r); return x ? x[key] || (Array.isArray(x) ? x : []) : []; }

const TABS = ["Evidence", "Predictions", "Expected Outcomes", "History", "Notes"];

export default function DecisionReview() {
  const { decisionId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Evidence");

  const queue = useBrainResource("/executive/queue", { params: { limit: 50 } });
  const outcomes = useBrainResource("/outcomes");
  const predictions = useBrainResource("/predictions/active");
  const evaluations = useBrainResource("/evaluations");
  const journal = useBrainResource("/journal", { params: { recommendation_id: decisionId } });

  const item = useMemo(() => (list(queue, "items") || []).find((i) => i.item_id === decisionId) || null, [queue.data, decisionId]); // eslint-disable-line react-hooks/exhaustive-deps
  const versions = useMemo(() => versionsForSubject(list(outcomes, "outcomes"), decisionId), [outcomes.data, decisionId]); // eslint-disable-line react-hooks/exhaustive-deps
  const latest = versions.length ? versions[versions.length - 1] : null;
  const linkedPreds = useMemo(() => {
    if (!latest || !latest.correlation_id) return [];
    return list(predictions, "predictions").filter((p) => p.correlation_id && p.correlation_id === latest.correlation_id);
  }, [latest, predictions.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const lessons = useBrainResource("/lessons");
  const moments = useMemo(() => (item ? {
    supersede: detectSupersede(versions, []),
    confidence: detectConfidenceDelta(versions),
    conflict: latest ? detectConflict(list(evaluations, "evaluations"), latest) : { present: false },
    knowledge: detectRelevantKnowledge(list(lessons, "lessons"), item),
  } : {}), [item, versions, latest, evaluations.data, lessons.data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (queue.status === "loading") return <LoadingState label="Opening the decision" rows={6} />;
  if (queue.status === "disabled") return <DisabledState capability="Executive Queue" flag="EXECUTIVE_QUEUE_ENABLED" />;
  if (!item) return <EmptyState title="Decision not found" detail={`No queue item ${decisionId}.`} testId="review-notfound" />;

  const impact = formatDecisionImpact(item);
  const journalEntries = list(journal, "entries");

  return (
    <div data-testid="decision-review" className="space-y-5">
      <button onClick={() => navigate("/brain")} className="text-xs text-slate-400 hover:text-slate-200" data-testid="review-back">← Back to Brain</button>

      <div className="rounded-xl border border-slate-800 bg-gradient-to-b from-slate-900/70 to-slate-950 p-5">
        <p className="text-[11px] uppercase tracking-wide text-slate-500">#{item.rank} · {item.business_unit} · {item.item_type}</p>
        <h1 className="mt-1 text-xl font-bold text-white">{item.title}</h1>
        <p className="mt-1 text-sm text-slate-400">
          {(item.confidence && item.confidence.tier) || "NA"} confidence · <span className={impact.isDollar ? "text-emerald-300" : "text-slate-300"}>{impact.display}</span>
          <span className="text-slate-500"> ({impact.basisLabel})</span>
        </p>
        <div className="mt-3"><MomentStrip moments={moments} /></div>
      </div>

      <div className="flex gap-1 border-b border-slate-800" role="tablist">
        {TABS.map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)} data-testid={`review-tab-${t.replace(/\s+/g, "-").toLowerCase()}`}
            className={`px-3 py-2 text-sm ${tab === t ? "border-b-2 border-emerald-500 font-medium text-emerald-300" : "text-slate-400 hover:text-slate-200"}`}>
            {t}{t === "Notes" ? <span className="ml-0.5 text-[8px] align-super text-slate-600">*</span> : null}
          </button>
        ))}
      </div>

      <div data-testid="review-panel">
        {tab === "Evidence" && (
          <div className="space-y-2">
            <p className="text-[11px] text-slate-500" title="MOCK — relevance % has no backend (Outcome Spine has no relevance score)">Evidence relevance % is a placeholder * · rows below are real Outcome-Spine data.</p>
            <EvidenceTrail evidence={latest ? latest.evidence : []} claims={latest ? latest.claims : []} evaluations={list(evaluations, "evaluations")} />
          </div>
        )}

        {tab === "Predictions" && (
          <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
            {linkedPreds.length === 0 ? <p className="text-xs text-slate-500">No correlation-linked predictions.</p> : (
              <ul className="space-y-1.5">{linkedPreds.map((p) => (<li key={p.prediction_id} className="text-sm text-slate-300">{p.prediction_type} · <span className="text-slate-500">{p.confidence_tier}</span></li>))}</ul>
            )}
          </div>
        )}

        {tab === "Expected Outcomes" && (
          <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4 text-sm text-slate-300">
            {outcomes.status === "disabled" ? <DisabledState capability="Outcome Spine" flag="OUTCOME_SPINE_ENABLED" /> : latest ? (
              <div className="space-y-1">
                <p>Expected: <span className="text-slate-100">{latest.expected ? String(latest.expected.value) : "—"}</span> <span className="text-slate-500">({latest.expected && latest.expected.basis})</span></p>
                <p>Actual: <span className="text-slate-100">{latest.actual ? String(latest.actual.value) : "—"}</span></p>
                <p>Result: <span className="text-slate-100">{latest.result || latest.status || "—"}</span></p>
              </div>
            ) : <p className="text-xs text-slate-500">No outcome recorded for this decision yet.</p>}
          </div>
        )}

        {tab === "History" && (
          <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
            {versions.length === 0 && journalEntries.length === 0 ? <p className="text-xs text-slate-500">No history yet.</p> : (
              <div className="space-y-3">
                {versions.length > 0 && (
                  <ul className="space-y-1">{versions.map((o) => (<li key={o.outcome_id} className="text-xs text-slate-300">v{o.version} · {o.status} · conf {(o.confidence && o.confidence.tier) || "—"}{o.result ? ` · ${o.result}` : ""}</li>))}</ul>
                )}
                {journalEntries.length > 0 && (
                  <ul className="space-y-1 border-t border-slate-800 pt-2">{journalEntries.map((e) => (<li key={e.entry_id} className="text-xs text-slate-400">journal · {e.decision_type} · {e.disposition || e.status_at_creation}</li>))}</ul>
                )}
              </div>
            )}
          </div>
        )}

        {tab === "Notes" && (
          <div className="rounded-lg border border-dashed border-slate-700 bg-slate-900/40 p-6 text-center" data-testid="review-notes-placeholder" title="MOCK placeholder — no notes backend">
            <p className="text-sm text-slate-400">{MOCK_NOTES.placeholder}</p>
          </div>
        )}
      </div>
      <p className="text-[9px] text-slate-600">* placeholder (mock) — see components/brain/mock/mockData.js</p>
    </div>
  );
}
